// Book Profitability Dashboard
// Real-time A/B/C book comparison with AI analytics

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  TrendingUp, TrendingDown, DollarSign, BarChart3, Settings2,
  RefreshCw, Calendar, Brain, AlertTriangle, Target, Zap,
  ChevronRight, ChevronDown, Loader2, Sparkles, ArrowUpRight,
  ArrowDownRight, Clock, Globe, Users, PieChart, Activity
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value || 0);
};

// Format percentage
const formatPercent = (value) => {
  return `${value >= 0 ? '+' : ''}${(value || 0).toFixed(1)}%`;
};

// Session colors
const sessionColors = {
  asian: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/30' },
  european: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30' },
  us: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
  all: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'border-cyan-500/30' }
};

// Book colors
const bookColors = {
  A: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500' },
  B: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500' },
  C: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500' }
};

// C-Book Configuration Dialog
const CBookConfigDialog = ({ configs, onConfigUpdate }) => {
  const [open, setOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [aRatio, setARatio] = useState(70);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!editingConfig) return;
    
    setSaving(true);
    try {
      await api.post('/api/profitability/cbook-config', {
        group_name: editingConfig.group_name,
        a_ratio: aRatio / 100,
        b_ratio: (100 - aRatio) / 100,
        group_title: `Hybrid ${aRatio}/${100 - aRatio}`
      });
      toast.success('C-Book configuration updated');
      setEditingConfig(null);
      onConfigUpdate();
    } catch (error) {
      toast.error('Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="cbook-config-btn">
          <Settings2 className="w-4 h-4" />
          C-Book Ratios
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-purple-400" />
            C-Book Hybrid Ratios Configuration
          </DialogTitle>
          <DialogDescription>
            Configure A-book / B-book allocation ratios for each MT5 group
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-80 overflow-y-auto">
          {configs.map((config) => (
            <div 
              key={config.group_name}
              className={`p-4 rounded-lg border transition-colors ${
                editingConfig?.group_name === config.group_name 
                  ? 'bg-purple-500/10 border-purple-500/50' 
                  : 'bg-white/5 border-white/10'
              }`}
            >
              {editingConfig?.group_name === config.group_name ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{config.group_name}</span>
                    <Badge variant="outline" className="text-purple-400">Editing</Badge>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>A-Book: {aRatio}%</span>
                      <span>B-Book: {100 - aRatio}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={aRatio}
                      onChange={(e) => setARatio(parseInt(e.target.value))}
                      className="w-full accent-purple-500"
                    />
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" onClick={handleSave} disabled={saving}>
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingConfig(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{config.group_name}</p>
                    <p className="text-sm text-muted-foreground">{config.group_title}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right text-sm">
                      <span className="text-emerald-400">{(config.a_ratio * 100).toFixed(0)}% A</span>
                      <span className="mx-1 text-muted-foreground">/</span>
                      <span className="text-red-400">{(config.b_ratio * 100).toFixed(0)}% B</span>
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => { setEditingConfig(config); setARatio(config.a_ratio * 100); }}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Book P&L Card
const BookPnLCard = ({ book, title, icon: Icon, colorClass }) => {
  if (!book) return null;
  
  return (
    <Card className={`glass border-white/10 border-l-4 ${colorClass.border}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className={`w-4 h-4 ${colorClass.text}`} />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className={`text-2xl font-bold ${book.net_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          {formatCurrency(book.net_pnl)}
        </p>
        <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
          <div>
            <span className="text-muted-foreground">Volume</span>
            <p className="font-medium">{formatCurrency(book.volume)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Trades</span>
            <p className="font-medium">{book.trade_count}</p>
          </div>
          {book.commissions > 0 && (
            <div>
              <span className="text-muted-foreground">Commissions</span>
              <p className="font-medium text-emerald-400">{formatCurrency(book.commissions)}</p>
            </div>
          )}
          {book.spreads > 0 && (
            <div>
              <span className="text-muted-foreground">Spreads</span>
              <p className="font-medium text-emerald-400">{formatCurrency(book.spreads)}</p>
            </div>
          )}
          {book.lp_costs > 0 && (
            <div>
              <span className="text-muted-foreground">LP Costs</span>
              <p className="font-medium text-red-400">-{formatCurrency(book.lp_costs)}</p>
            </div>
          )}
          {book.client_losses > 0 && (
            <div>
              <span className="text-muted-foreground">Client Losses</span>
              <p className="font-medium text-emerald-400">{formatCurrency(book.client_losses)}</p>
            </div>
          )}
          {book.client_wins > 0 && (
            <div>
              <span className="text-muted-foreground">Client Wins</span>
              <p className="font-medium text-red-400">-{formatCurrency(book.client_wins)}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// AI Insights Panel
const AIInsightsPanel = ({ insights, loading, onRefresh }) => {
  const [expanded, setExpanded] = useState(true);

  if (loading) {
    return (
      <Card className="glass border-white/10 border-l-4 border-l-cyan-500">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-400 mr-2" />
          <span>Generating AI insights...</span>
        </CardContent>
      </Card>
    );
  }

  if (!insights) return null;

  return (
    <Card className="glass border-white/10 border-l-4 border-l-cyan-500">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Brain className="w-5 h-5 text-cyan-400" />
            AI Analytics Insights
            <Sparkles className="w-4 h-4 text-amber-400" />
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onRefresh}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
              <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? '' : '-rotate-90'}`} />
            </Button>
          </div>
        </div>
        <CardDescription>
          Powered by Gemini AI • {insights.data_period}
        </CardDescription>
      </CardHeader>
      
      {expanded && (
        <CardContent className="space-y-4">
          {/* Executive Summary */}
          <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
            <p className="text-sm font-medium text-cyan-400 mb-2">Executive Summary</p>
            <p className="text-sm text-muted-foreground">{insights.executive_summary}</p>
          </div>

          {/* Metrics Row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-white/5 text-center">
              <p className="text-xs text-muted-foreground">Sharpe Ratio</p>
              <p className={`text-xl font-bold ${insights.sharpe_ratio > 1 ? 'text-emerald-400' : 'text-amber-400'}`}>
                {insights.sharpe_ratio.toFixed(2)}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-white/5 text-center">
              <p className="text-xs text-muted-foreground">Risk-Adj Return</p>
              <p className={`text-xl font-bold ${insights.risk_adjusted_return >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {formatCurrency(insights.risk_adjusted_return)}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-white/5 text-center">
              <p className="text-xs text-muted-foreground">Volatility</p>
              <p className="text-xl font-bold text-amber-400">{formatCurrency(insights.volatility)}</p>
            </div>
          </div>

          {/* Trend Predictions */}
          {insights.trend_predictions?.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                Trend Prediction
              </p>
              {insights.trend_predictions.map((trend, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm">Next Session P&L Forecast</span>
                    <Badge variant="outline" className={trend.predicted_change_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                      {formatPercent(trend.predicted_change_pct)}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{trend.recommendation}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Confidence:</span>
                    <Progress value={trend.confidence * 100} className="flex-1 h-1" />
                    <span className="text-xs">{(trend.confidence * 100).toFixed(0)}%</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Anomalies */}
          {insights.anomalies?.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                Anomaly Alerts ({insights.anomalies.length})
              </p>
              {insights.anomalies.map((anomaly, idx) => (
                <div 
                  key={idx} 
                  className={`p-3 rounded-lg border ${
                    anomaly.severity === 'critical' ? 'bg-red-500/10 border-red-500/50' :
                    anomaly.severity === 'high' ? 'bg-amber-500/10 border-amber-500/50' :
                    'bg-yellow-500/10 border-yellow-500/30'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{anomaly.group_name} - {anomaly.book_type}-Book</span>
                    <Badge className={
                      anomaly.severity === 'critical' ? 'bg-red-500' :
                      anomaly.severity === 'high' ? 'bg-amber-500' : 'bg-yellow-500'
                    }>
                      {anomaly.severity}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{anomaly.description}</p>
                  <p className="text-xs text-cyan-400 mt-1">→ {anomaly.suggested_action}</p>
                </div>
              ))}
            </div>
          )}

          {/* Recommendations */}
          {insights.recommendations?.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <Target className="w-4 h-4 text-purple-400" />
                Optimization Recommendations
              </p>
              {insights.recommendations.map((rec, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                  <p className="text-sm font-medium mb-1">{rec.suggested_change}</p>
                  <p className="text-xs text-muted-foreground mb-2">
                    Affected: {rec.affected_groups.join(', ')} • Current: {rec.current_state}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-emerald-400">Projected: {rec.projected_impact}</span>
                    <span className="text-xs text-muted-foreground">
                      Confidence: {(rec.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Profit Attribution */}
          {insights.profit_attribution?.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <PieChart className="w-4 h-4 text-cyan-400" />
                Profit Source Attribution
              </p>
              <div className="space-y-1">
                {insights.profit_attribution.map((source, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span>{source.source}</span>
                        <span className={bookColors[source.book_type]?.text}>{source.percentage}%</span>
                      </div>
                      <Progress value={source.percentage} className="h-1" />
                    </div>
                    <span className="text-xs w-20 text-right">{formatCurrency(source.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Benchmarks */}
          {insights.benchmarks?.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <Activity className="w-4 h-4 text-blue-400" />
                Industry Benchmarks
              </p>
              <div className="grid grid-cols-2 gap-2">
                {insights.benchmarks.map((bench, idx) => (
                  <div key={idx} className="p-3 rounded-lg bg-white/5">
                    <p className="text-xs text-muted-foreground mb-1">{bench.metric}</p>
                    <div className="flex items-center justify-between">
                      <span className={`font-bold ${
                        bench.status === 'above' ? 'text-emerald-400' :
                        bench.status === 'below' ? 'text-red-400' : 'text-amber-400'
                      }`}>
                        {bench.your_value.toFixed(2)}%
                      </span>
                      <span className="text-xs text-muted-foreground">
                        vs {bench.benchmark_value.toFixed(2)}%
                      </span>
                    </div>
                    <Badge 
                      variant="outline" 
                      className={`mt-1 text-xs ${
                        bench.status === 'above' ? 'text-emerald-400 border-emerald-500/30' :
                        bench.status === 'below' ? 'text-red-400 border-red-500/30' : ''
                      }`}
                    >
                      {bench.status === 'above' ? '↑ Above' : bench.status === 'below' ? '↓ Below' : '= At Par'}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

// Main Book Profitability Page
const BookProfitability = () => {
  const { user } = useAuth();
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [session, setSession] = useState('all');
  const [comparisonMode, setComparisonMode] = useState('all');
  const [selectedGroups, setSelectedGroups] = useState('all');
  
  const [dashboard, setDashboard] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [cbookConfigs, setCbookConfigs] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  const [expandedGroups, setExpandedGroups] = useState({});

  const isAdmin = user?.role === 'admin';

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    try {
      const response = await api.get('/api/profitability/dashboard', {
        params: {
          date_from: dateFrom,
          date_to: dateTo,
          session,
          comparison_mode: comparisonMode,
          groups: selectedGroups
        }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load profitability data');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, dateFrom, dateTo, session, comparisonMode, selectedGroups]);

  // Fetch AI insights
  const fetchAiInsights = useCallback(async () => {
    if (!isAdmin) return;
    
    setAiLoading(true);
    try {
      const response = await api.get('/api/profitability/ai-insights', {
        params: {
          date_from: dateFrom,
          date_to: dateTo,
          historical_days: 30
        }
      });
      setAiInsights(response.data);
    } catch (error) {
      console.error('Failed to fetch AI insights:', error);
      toast.error('Failed to generate AI insights');
    } finally {
      setAiLoading(false);
    }
  }, [isAdmin, dateFrom, dateTo]);

  // Fetch C-book configs
  const fetchCbookConfigs = useCallback(async () => {
    if (!isAdmin) return;
    
    try {
      const response = await api.get('/api/profitability/cbook-config');
      setCbookConfigs(response.data);
    } catch (error) {
      console.error('Failed to fetch C-book configs:', error);
    }
  }, [isAdmin]);

  // Initial load
  useEffect(() => {
    if (isAdmin) {
      fetchDashboard();
      fetchCbookConfigs();
    }
  }, [isAdmin, fetchDashboard, fetchCbookConfigs]);

  // Auto-refresh AI insights every 15 minutes
  useEffect(() => {
    if (isAdmin && dashboard) {
      fetchAiInsights();
      
      const interval = setInterval(fetchAiInsights, 15 * 60 * 1000); // 15 minutes
      return () => clearInterval(interval);
    }
  }, [isAdmin, dashboard, fetchAiInsights]);

  // Manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboard();
    setRefreshing(false);
  };

  // Toggle group expansion
  const toggleGroupExpand = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="glass border-white/10 p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">Admin access required for Book Profitability</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="book-profitability-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-cyan-400" />
            Book Profitability Dashboard
          </h1>
          <p className="text-muted-foreground">Real-time A/B/C book comparison with AI analytics</p>
        </div>
        <div className="flex items-center gap-2">
          <CBookConfigDialog configs={cbookConfigs} onConfigUpdate={fetchCbookConfigs} />
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => fetchAiInsights()}
            disabled={aiLoading}
            className="gap-2"
          >
            <Brain className="w-4 h-4" />
            {aiLoading ? 'Analyzing...' : 'AI Insights'}
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="glass border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Date Range */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-2 py-1 bg-white/5 border border-white/10 rounded text-sm"
              />
              <span className="text-muted-foreground">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-2 py-1 bg-white/5 border border-white/10 rounded text-sm"
              />
            </div>

            {/* Session Toggle */}
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4 text-muted-foreground mr-1" />
              {['all', 'asian', 'european', 'us'].map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={session === s ? 'default' : 'ghost'}
                  onClick={() => setSession(s)}
                  className={`text-xs ${session === s ? sessionColors[s].bg : ''}`}
                >
                  {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                </Button>
              ))}
            </div>

            {/* Comparison Mode */}
            <div className="flex items-center gap-1">
              <Globe className="w-4 h-4 text-muted-foreground mr-1" />
              <select
                value={comparisonMode}
                onChange={(e) => setComparisonMode(e.target.value)}
                className="px-2 py-1 bg-white/5 border border-white/10 rounded text-sm"
              >
                <option value="all">All Books</option>
                <option value="a_vs_b">A vs B</option>
                <option value="b_vs_c">B vs C</option>
                <option value="a_vs_c">A vs C</option>
                <option value="a_vs_b_vs_c">A vs B vs C</option>
              </select>
            </div>

            <Button size="sm" onClick={fetchDashboard} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      ) : dashboard && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Total Broker P&L */}
            <Card className="glass border-white/10 border-t-4 border-t-cyan-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Broker P&L</p>
                    <p className={`text-3xl font-bold ${dashboard.total_broker_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {formatCurrency(dashboard.total_broker_pnl)}
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 text-cyan-400" />
                </div>
                <div className="flex items-center gap-1 mt-2">
                  {dashboard.total_broker_pnl >= 0 ? (
                    <ArrowUpRight className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 text-red-400" />
                  )}
                  <span className="text-xs text-muted-foreground">
                    {dashboard.active_session?.session_type} session active
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Book Summaries */}
            <BookPnLCard 
              book={dashboard.a_book_summary} 
              title="A-Book (STP/ECN)" 
              icon={Zap}
              colorClass={bookColors.A}
            />
            <BookPnLCard 
              book={dashboard.b_book_summary} 
              title="B-Book (Market Maker)" 
              icon={Users}
              colorClass={bookColors.B}
            />
            <BookPnLCard 
              book={dashboard.c_book_summary} 
              title="C-Book (Hybrid)" 
              icon={Activity}
              colorClass={bookColors.C}
            />
          </div>

          {/* AI Insights */}
          <AIInsightsPanel 
            insights={aiInsights} 
            loading={aiLoading}
            onRefresh={fetchAiInsights}
          />

          {/* Groups Breakdown */}
          <Card className="glass border-white/10">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-400" />
                MT5 Group Breakdown
              </CardTitle>
              <CardDescription>
                Click a group to see detailed book comparison
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {dashboard.groups.map((group) => (
                  <div key={group.group_name} className="border border-white/10 rounded-lg overflow-hidden">
                    {/* Group Header */}
                    <button
                      onClick={() => toggleGroupExpand(group.group_name)}
                      className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <ChevronRight className={`w-4 h-4 transition-transform ${expandedGroups[group.group_name] ? 'rotate-90' : ''}`} />
                        <div className="text-left">
                          <span className="font-semibold text-cyan-400">{group.group_name}</span>
                          <p className="text-xs text-muted-foreground">{group.group_title}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">{group.client_count} clients</Badge>
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        {group.a_book && (
                          <div className="text-right">
                            <span className="text-xs text-muted-foreground">A-Book</span>
                            <p className={`font-medium ${group.a_book.net_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {formatCurrency(group.a_book.net_pnl)}
                            </p>
                          </div>
                        )}
                        {group.b_book && (
                          <div className="text-right">
                            <span className="text-xs text-muted-foreground">B-Book</span>
                            <p className={`font-medium ${group.b_book.net_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {formatCurrency(group.b_book.net_pnl)}
                            </p>
                          </div>
                        )}
                        {group.c_book && (
                          <div className="text-right">
                            <span className="text-xs text-muted-foreground">C-Book</span>
                            <p className={`font-medium ${group.c_book.net_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {formatCurrency(group.c_book.net_pnl)}
                            </p>
                          </div>
                        )}
                        <div className="text-right min-w-24">
                          <span className="text-xs text-muted-foreground">Total</span>
                          <p className={`font-bold ${group.total_broker_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {formatCurrency(group.total_broker_pnl)}
                          </p>
                        </div>
                      </div>
                    </button>

                    {/* Expanded Details */}
                    {expandedGroups[group.group_name] && (
                      <div className="bg-black/30 border-t border-white/10 p-4">
                        <div className="grid grid-cols-3 gap-4">
                          {/* A-Book Details */}
                          {group.a_book && (
                            <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                              <p className="font-medium text-emerald-400 mb-3">A-Book (STP/ECN)</p>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Volume</span>
                                  <span>{formatCurrency(group.a_book.volume)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Commissions</span>
                                  <span className="text-emerald-400">+{formatCurrency(group.a_book.commissions)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Spreads</span>
                                  <span className="text-emerald-400">+{formatCurrency(group.a_book.spreads)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">LP Costs</span>
                                  <span className="text-red-400">-{formatCurrency(group.a_book.lp_costs)}</span>
                                </div>
                                <div className="flex justify-between border-t border-white/10 pt-2 font-medium">
                                  <span>Net P&L</span>
                                  <span className={group.a_book.net_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                                    {formatCurrency(group.a_book.net_pnl)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* B-Book Details */}
                          {group.b_book && (
                            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                              <p className="font-medium text-red-400 mb-3">B-Book (Market Maker)</p>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Volume</span>
                                  <span>{formatCurrency(group.b_book.volume)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Client Losses</span>
                                  <span className="text-emerald-400">+{formatCurrency(group.b_book.client_losses)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Client Wins</span>
                                  <span className="text-red-400">-{formatCurrency(group.b_book.client_wins)}</span>
                                </div>
                                <div className="flex justify-between border-t border-white/10 pt-2 font-medium">
                                  <span>Net P&L</span>
                                  <span className={group.b_book.net_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                                    {formatCurrency(group.b_book.net_pnl)}
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2 italic">
                                  * Broker profits when clients lose
                                </p>
                              </div>
                            </div>
                          )}

                          {/* C-Book Details */}
                          {group.c_book && (
                            <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                              <p className="font-medium text-purple-400 mb-1">C-Book (Hybrid)</p>
                              <p className="text-xs text-muted-foreground mb-3">
                                Ratio: {(group.c_book_ratio?.a_ratio * 100).toFixed(0)}% A / {(group.c_book_ratio?.b_ratio * 100).toFixed(0)}% B
                              </p>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">A-Component</span>
                                  <span>{formatCurrency(group.c_book.commissions + group.c_book.spreads - group.c_book.lp_costs)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">B-Component</span>
                                  <span>{formatCurrency(group.c_book.client_losses - group.c_book.client_wins)}</span>
                                </div>
                                <div className="flex justify-between border-t border-white/10 pt-2 font-medium">
                                  <span>Net P&L</span>
                                  <span className={group.c_book.net_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                                    {formatCurrency(group.c_book.net_pnl)}
                                  </span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2 italic">
                                  * ({(group.c_book_ratio?.a_ratio * 100).toFixed(0)}% × A-P&L) + ({(group.c_book_ratio?.b_ratio * 100).toFixed(0)}% × B-P&L)
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Formula Reference */}
          <Card className="glass border-white/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Profitability Formulas (Broker View)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-xs">
                <div className="p-3 rounded-lg bg-emerald-500/10">
                  <p className="font-medium text-emerald-400 mb-1">A-Book</p>
                  <p className="text-muted-foreground">Commissions + Spreads - LP Costs</p>
                </div>
                <div className="p-3 rounded-lg bg-red-500/10">
                  <p className="font-medium text-red-400 mb-1">B-Book</p>
                  <p className="text-muted-foreground">Client Losses - Client Wins</p>
                </div>
                <div className="p-3 rounded-lg bg-purple-500/10">
                  <p className="font-medium text-purple-400 mb-1">C-Book</p>
                  <p className="text-muted-foreground">(A-ratio × A-P&L) + (B-ratio × B-P&L)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default BookProfitability;
